import { useState, useEffect, useRef } from "react";
import zoomSdk from "@zoom/appssdk";
import { ROLES, BUILT_IN_AGENT, SUMMARIZE_STATES, VIEWS, SESSION_TABS, RTMS_REQUESTER_DEFAULT_NAME } from "../../constants";
import {
  SCHEDULED_AGENTS_ALL_INACTIVE_MESSAGE,
  AGENT_TOGGLE_ERROR_MESSAGE,
  STARTED_BY_DISMISS_MS,
} from "./home.constants";
import LogManager from "../../utils/logManager";
import { onSummarize, onEndSummarize, createRTMSChangeHandler } from "./home.utils";
import { truncateDisplayName, scheduledAgentsPartialErrorMessage } from "../../utils/agentDisplayUtils";
import {
  fetchAvailableExtensions,
  fetchActiveAgents,
  enableExtension,
  disableExtension,
} from "../../utils/agenticExtensionUtils";
import useAutoActivateScheduledAgents from "../../hooks/useAutoActivateScheduledAgents";
import { usePublishedContent } from "../../hooks/usePublishedContent";
import { useWebSocket } from "../../hooks/useWebSocket";
import { useInsights } from "../insights/insights.hooks";

/**
 * useAgenticExtensions - owns the agentic-assistant extension catalog and the enabled-agent set:
 * fetching available/active agents, auto-activating the organizer's schedule-time selection, the
 * organizer toggle, and the partial-failure / all-inactive toasts. Extracted from Home.jsx.
 *
 * Toasts are surfaced through the injected `emitToast(message)` so Home keeps a single toast queue
 * shared with non-agentic sources (e.g. preference-save failures).
 *
 * @returns {{ availableExtensions, enabledAgentIds, agentsLoading, applyActiveAgents,
 *             onToggleAgent, onPopoverOpen }}
 */
export const useAgenticExtensions = ({
  isAssistantEnabled,
  role,
  meetingUuid,
  meetingId,
  rtmsStreamId,
  summarizeState,
  emitToast,
}) => {
  const [availableExtensions, setAvailableExtensions] = useState([]);
  const [enabledAgentIds, setEnabledAgentIds] = useState([BUILT_IN_AGENT.id]);
  const [agentsLoading, setAgentsLoading] = useState(false);

  // Scheduled agents currently active this session (auto-activated or manually toggled on) —
  // merged into enabledAgentIds so a late-resolving pre-activation /available-extensions fetch
  // can't drop them. Reset on each session boundary so a fresh snapshot is authoritative.
  const scheduledActivatedIdsRef = useRef([]);
  // Failed scheduled-agent ids awaiting a partial-failure toast: set when a partial failure is
  // reported before the /available-extensions catalog has resolved (names can't resolve yet);
  // flushed with names once that fetch resolves.
  const pendingPartialFailedIdsRef = useRef(null);
  // The /available-extensions catalog once its fetch resolves — the authoritative "loaded"
  // signal: null = not resolved yet, [] = resolved but empty, [...] = resolved with rows. Using
  // this (not availableExtensions.length) distinguishes "not loaded" from "loaded-empty", so a
  // queued partial-failure toast is never stranded when the fetch resolves empty.
  const resolvedCatalogRef = useRef(null);

  // Build + show the Case-2 partial-failure toast, resolving failed ids to (truncated) display
  // names from the supplied catalog. A name that can't be resolved is dropped; the message
  // helper falls back to a name-free "some agents are inactive" when none resolve.
  const emitPartialFailureToast = (failedIds, extensions) => {
    const failedNames = (failedIds ?? [])
      .map((id) => {
        const ext = extensions.find((e) => e.extensionId === id);
        return truncateDisplayName(ext?.displayName || ext?.defaultDisplayName);
      })
      .filter(Boolean);
    emitToast(scheduledAgentsPartialErrorMessage(failedNames));
  };

  // Apply a fresh /active-agents result. `agents` is null when the fetch failed —
  // keep the previous list in that case rather than blanking every agent.
  const applyActiveAgents = (agents) => {
    if (!agents) return;
    setAvailableExtensions(agents);
    setEnabledAgentIds([BUILT_IN_AGENT.id, ...agents.map((a) => a.extensionId)]);
  };

  // Reset the per-session tracking refs whenever the session is not active, so each new
  // SUMMARIZING episode's /available-extensions snapshot is authoritative (a stale ref must
  // not re-add an agent an external actor deactivated between sessions).
  useEffect(() => {
    if (summarizeState !== SUMMARIZE_STATES.SUMMARIZING) {
      scheduledActivatedIdsRef.current = [];
      pendingPartialFailedIdsRef.current = null;
      resolvedCatalogRef.current = null;
    }
  }, [summarizeState]);

  // Fetch available/active extensions once session is active (Assistant on only)
  useEffect(() => {
    if (!isAssistantEnabled) return;
    if (!role || !meetingUuid) return;
    if (summarizeState !== SUMMARIZE_STATES.SUMMARIZING) return;

    let stale = false;
    if (role === ROLES.ORGANIZER) {
      fetchAvailableExtensions(meetingUuid).then((extensions) => {
        if (stale) return;
        setAvailableExtensions(extensions);
        const enabledFromApi = extensions
          .filter((ext) => ext.enabled)
          .map((ext) => ext.extensionId);
        // Union in any scheduled agents already auto-activated this session: this fetch may
        // have been issued pre-activation but resolve after it, and its snapshot would
        // otherwise report them as off and drop them.
        setEnabledAgentIds([BUILT_IN_AGENT.id, ...new Set([...enabledFromApi, ...scheduledActivatedIdsRef.current])]);
        // Mark the catalog resolved (even if empty) so a later partial failure emits immediately
        // instead of queueing forever, then flush any toast queued before this resolved — with
        // names from this fresh result (an empty result yields the name-free fallback).
        resolvedCatalogRef.current = extensions;
        if (pendingPartialFailedIdsRef.current) {
          emitPartialFailureToast(pendingPartialFailedIdsRef.current, extensions);
          pendingPartialFailedIdsRef.current = null;
        }
      });
    } else {
      fetchActiveAgents(meetingUuid).then((agents) => {
        if (stale) return;
        applyActiveAgents(agents);
      });
    }
    return () => { stale = true; };
  }, [isAssistantEnabled, role, meetingUuid, summarizeState]);

  // Auto-activate the organizer's schedule-time agent selection once the session is active.
  // It only reacts to an already-active session.
  useAutoActivateScheduledAgents({
    isAssistantEnabled,
    role,
    meetingId,
    meetingUuid,
    aimsUuid: rtmsStreamId,
    summarizeState,
    onAgentsActivated: (activatedIds) => {
      // Merge locally (like the manual toggle) instead of re-fetching, and record the ids so the
      // organizer /available-extensions effect's write can't drop them (see scheduledActivatedIdsRef).
      // Activated agents intentionally stay ACTIVE past session end so they still receive the
      // post-summary event — they are only turned off by an explicit manual toggle.
      scheduledActivatedIdsRef.current = [...new Set([...scheduledActivatedIdsRef.current, ...activatedIds])];
      setEnabledAgentIds((prev) => [...new Set([...prev, ...activatedIds])]);
    },
    onError: (info) => {
      if (info?.scope === "partial") {
        // Case 2 (partial): name the failed agents from the /available-extensions catalog.
        // If the catalog hasn't resolved yet (its fetch races Phase-2 activation on SUMMARIZING),
        // queue the ids and let the catalog effect emit the named toast once it resolves — instead
        // of degrading to a name-free message. resolvedCatalogRef (not availableExtensions.length)
        // is the load signal, so a catalog that resolved *empty* still emits now (generic) rather
        // than queueing a toast that would never flush.
        if (resolvedCatalogRef.current) {
          emitPartialFailureToast(info.failedIds, resolvedCatalogRef.current);
        } else {
          pendingPartialFailedIdsRef.current = info.failedIds ?? [];
        }
        return;
      }
      // Case 1 (fetch failed) and Case 3 (all failed) → "all inactive".
      emitToast(SCHEDULED_AGENTS_ALL_INACTIVE_MESSAGE);
    },
  });

  const onPopoverOpen = () => {
    if (role !== ROLES.ORGANIZER && meetingUuid) {
      setAgentsLoading(true);
      fetchActiveAgents(meetingUuid).then(applyActiveAgents).finally(() => setAgentsLoading(false));
    }
  };

  const onToggleAgent = async (id, checked) => {
    if (id === BUILT_IN_AGENT.id) return;
    if (checked && !rtmsStreamId) return;
    const toggleAgent = (prev, add) =>
      add ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((x) => x !== id);
    setEnabledAgentIds((prev) => toggleAgent(prev, checked));
    const result = checked
      ? await enableExtension(meetingUuid, meetingId, id, rtmsStreamId)
      : await disableExtension(meetingUuid, id);
    if (!result.success) {
      // Revert the UI; leave scheduledActivatedIdsRef untouched — a failed disable
      // means the agent is still ACTIVE, so it must stay tracked.
      setEnabledAgentIds((prev) => toggleAgent(prev, !checked));
      emitToast(AGENT_TOGGLE_ERROR_MESSAGE);
      return;
    }
    // Keep scheduledActivatedIdsRef in sync with the backend so the
    // /available-extensions union neither drops nor resurrects this agent: remove it
    // once a disable is confirmed, re-add it once an enable is confirmed.
    if (!checked) {
      scheduledActivatedIdsRef.current = scheduledActivatedIdsRef.current.filter((x) => x !== id);
    } else if (!scheduledActivatedIdsRef.current.includes(id)) {
      scheduledActivatedIdsRef.current = [...scheduledActivatedIdsRef.current, id];
    }
  };

  return {
    availableExtensions,
    enabledAgentIds,
    agentsLoading,
    applyActiveAgents,
    onToggleAgent,
    onPopoverOpen,
  };
};

/**
 * useRealtimeStores - owns the single WebSocket connection and the real-time stores it feeds:
 * the published-content store (UX Publish) and the insight store (action items + agenda), plus
 * the on-session-start seed of both insight lists. Extracted from Home.jsx.
 *
 * The WebSocket is a single connection for all push (UX Publish + Insight Push). It's activated
 * only while summarizing with Assistant on (the insight-push flag is an Assistant feature, so it
 * can't turn the socket on by itself). wsConnected is driven off the
 * real socket lifecycle (open/close), not intent, so fallback polling stays active when the
 * handshake fails / retries / gives up.
 *
 * @returns {{ publishedContentStore, insightStore }}
 */
export const useRealtimeStores = ({
  meetingUuid,
  isAssistantEnabled,
  summarizeState,
  meetingAgendaActive,
  meetingActionItemsActive,
}) => {
  const isSummarizing = summarizeState === SUMMARIZE_STATES.SUMMARIZING;
  const wsEnabled = isAssistantEnabled && isSummarizing;
  const publishedContentStore = usePublishedContent(meetingUuid, wsEnabled);

  // Insight store: receives WebSocket push messages, maintains action items + agenda state, and
  // seeds both on session start.
  const insightStore = useInsights({
    meetingUuid,
    isSessionActive: isSummarizing,
    meetingAgendaActive,
    meetingActionItemsActive,
  });

  // WebSocket message dispatcher — routes by data.type.
  const handleWsMessage = (data) => {
    // Gate ux_publish on Assistant so dispatch stays in lockstep with the (Assistant-only)
    // catch-up fetch and Assistant-off sessions don't write a partial published-content store.
    if (data.type === "ux_publish" && isAssistantEnabled) {
      publishedContentStore.handlePublishMessage(data);
    } else if (data.type === "insight_update") {
      insightStore.handleInsightMessage(data);
    }
  };

  useWebSocket(wsEnabled ? meetingUuid : null, handleWsMessage, {
    onConnectionChange: insightStore.setWsConnected,
  });

  return { publishedContentStore, insightStore };
};

/**
 * useSummarizationController - owns the in-meeting RTMS/summarization state machine that was
 * previously inlined in Home.jsx: the summarize + rtms-error state cluster, the timer/session
 * refs, the ref-sync effects, the Zoom RTMS status-change listener, and the start()/end()
 * actions. Keeping it here makes Home a thin view and gives the machine a single testable home.
 *
 * `start({ isRestartAttempt })` and `end()` wrap the onSummarize/onEndSummarize orchestration so
 * callers (DefaultView, ErrorModals, the auto-start / end-button paths) no longer thread ~15
 * setters + refs by hand. State is seeded from `initialSession` (the RTMS session already running
 * when the app opened). Shared by the summary and transcribe flows: `isTranscribe` posts the start
 * message to chat, `isPerHost` only follows this user's own stream, and `isAutostarting` (ZMDS
 * transcript auto-start) starts on mount.
 *
 * @returns state values, the feedback-modal setter, the error-modal bundle, the session refs read
 *          during render, and the start/end actions.
 */
export const useSummarizationController = ({
  meetingUuid,
  meetingId,
  role,
  alias,
  appContext,
  isTranscribe,
  isPerHost,
  isAutostarting,
  initialSession,
}) => {
  // Seeded from the RTMS session already running when the app opened (LOADING when auto-starting).
  const [summarizeState, setSummarizeState] = useState(
    isAutostarting ? SUMMARIZE_STATES.LOADING : initialSession.summarizeState
  );
  const [isRtmsStartedByOther, setIsRtmsStartedByOther] = useState(initialSession.isRtmsStartedByOther);
  const [rtmsRequesterName, setRtmsRequesterName] = useState(initialSession.rtmsRequesterName);
  const [meetingAgendaActive, setMeetingAgendaActive] = useState(initialSession.meetingAgendaActive);
  const [meetingActionItemsActive, setMeetingActionItemsActive] = useState(initialSession.meetingActionItemsActive);
  const [rtmsStreamId, setRtmsStreamId] = useState(initialSession.rtmsStreamId);
  const [summarizeClickCount, setSummarizeClickCount] = useState(0);
  const [isServerErrorModalOpen, setIsServerErrorModalOpen] = useState(false);
  const [isRtmsErrorModalOpen, setIsRtmsErrorModalOpen] = useState(false);
  const [rtmsErrorType, setRtmsErrorType] = useState(null);
  const [isProductFeedbackModalOpen, setIsProductFeedbackModalOpen] = useState(false);
  // TODO: aimsUuid/summaryData are set by checkForSummary once the READY-state UI lands.
  const [aimsUuid, setAimsUuid] = useState(null);
  const [summaryData, setSummaryData] = useState(null);

  // Refs mirroring state for use inside async callbacks, plus RTMS session-control timers.
  const summarizeStateRef = useRef(summarizeState);
  const isRtmsStartedByOtherRef = useRef(isRtmsStartedByOther);
  const rtmsStartTimestampRef = useRef(initialSession.rtmsStartTime);
  const hideStoppedComponentTimerRef = useRef(null);
  const sessionStatusIntervalRef = useRef(null);
  const sessionStatusTimeoutRef = useRef(null);
  const rejectionTimeoutRef = useRef(null);
  // Whether this user's start is in flight, and the rtmsSessionId of their stream (per-host mode)
  const userInitiatedStartRef = useRef(false);
  const rtmsSessionIdRef = useRef(null);

  // Keep summarizeStateRef in sync, and stop the start timers that no longer apply.
  useEffect(() => {
    summarizeStateRef.current = summarizeState;
    // The session is up: cancel the start timeout and the rejected-start reset (someone else's start
    // went through). The start poll keeps running until ACTIVE, since it is what fills in the stream
    // id and agenda/action-items flags (the only source in per-host mode).
    if (summarizeState === SUMMARIZE_STATES.SUMMARIZING) {
      clearTimeout(sessionStatusTimeoutRef.current);
      clearTimeout(rejectionTimeoutRef.current);
    } else if (summarizeState !== SUMMARIZE_STATES.LOADING) {
      // Stopped or reset before the poll saw ACTIVE: it would never see it now, and the start
      // timeout would raise an RTMS error over the stopped screen, so stop both.
      clearInterval(sessionStatusIntervalRef.current);
      clearTimeout(sessionStatusTimeoutRef.current);
    }
  }, [summarizeState]);

  useEffect(() => {
    isRtmsStartedByOtherRef.current = isRtmsStartedByOther;
  }, [isRtmsStartedByOther]);

  // Set up the RTMS status change listener, then auto-start if requested.
  useEffect(() => {
    if (meetingUuid) {
      try {
        LogManager.info(`Setting up RTMS status change listener for meeting: ${meetingUuid}`);
        const handleRTMSChange = createRTMSChangeHandler({
          meetingUUID: meetingUuid,
          alias,
          setSummarizeState,
          setIsRtmsStartedByOther,
          setRtmsRequesterName,
          setMeetingAgendaActive,
          setMeetingActionItemsActive,
          setRtmsStreamId,
          setRtmsErrorType,
          setIsRtmsErrorModalOpen,
          setIsProductFeedbackModalOpen,
          setAimsUuid,
          setSummaryData,
          summarizeStateRef,
          isRtmsStartedByOtherRef,
          rtmsStartTimestampRef,
          hideStoppedComponentTimerRef,
          isTranscribe,
          isPerHost,
          userInitiatedStartRef,
          rtmsSessionIdRef,
        });
        zoomSdk.onRTMSStatusChange(handleRTMSChange);
      } catch (error) {
        LogManager.error("Error setting up RTMS status listener:", error);
      }
    }

    if (isAutostarting) {
      LogManager.info("Auto start triggered, starting transcription automatically...");
      start({ isAutostart: true });
    }

    return () => {
      if (hideStoppedComponentTimerRef.current) clearTimeout(hideStoppedComponentTimerRef.current);
      if (sessionStatusIntervalRef.current) clearInterval(sessionStatusIntervalRef.current);
      if (sessionStatusTimeoutRef.current) clearTimeout(sessionStatusTimeoutRef.current);
      if (rejectionTimeoutRef.current) clearTimeout(rejectionTimeoutRef.current);
    };
    // Run once on mount.
  }, []);

  const start = ({ isRestartAttempt = false, isAutostart = false } = {}) =>
    onSummarize({
      summarizeState,
      setSummarizeState,
      setSummarizeClickCount,
      setRtmsRequesterName,
      setMeetingAgendaActive,
      setMeetingActionItemsActive,
      setRtmsStreamId,
      setRtmsErrorType,
      setIsRtmsErrorModalOpen,
      setIsServerErrorModalOpen,
      meetingUUID: meetingUuid,
      meetingId,
      role,
      appContext,
      isRestartAttempt,
      isAutostart,
      userInitiatedStartRef,
      sessionStatusIntervalRef,
      sessionStatusTimeoutRef,
      rejectionTimeoutRef,
      rtmsStartTimestampRef,
    });

  const end = () => {
    // Stop the start poll, or a late ACTIVE poll would flip this End back to SUMMARIZING.
    clearInterval(sessionStatusIntervalRef.current);
    clearTimeout(sessionStatusTimeoutRef.current);
    return onEndSummarize({
      summarizeState,
      setSummarizeState,
      setIsServerErrorModalOpen,
      meetingUUID: meetingUuid,
      meetingId,
      role,
      appContext,
    });
  };

  // Reset the summarize/rtms state back to DEFAULT after an RTMS error is dismissed. Shared by
  // the cancel and restart paths; restart additionally re-runs start().
  const resetAfterRtmsError = () => {
    setIsRtmsErrorModalOpen(false);
    setRtmsErrorType(null);
    setSummarizeState(SUMMARIZE_STATES.DEFAULT);
    setIsRtmsStartedByOther(false);
    setRtmsRequesterName(null);
    setMeetingAgendaActive(false);
    setMeetingActionItemsActive(false);
  };

  // Error-modal cluster: state the modals render off, plus the transitions they trigger. Bundled
  // so Home spreads it straight into <ErrorModals> instead of threading raw setters.
  const errorModals = {
    isServerErrorModalOpen,
    isRtmsErrorModalOpen,
    rtmsErrorType,
    onDismissServerError: () => setIsServerErrorModalOpen(false),
    onCancelRtmsError: () => {
      resetAfterRtmsError();
      LogManager.info("User cancelled RTMS error modal - reset to original state");
    },
    onRestartRtmsError: () => {
      resetAfterRtmsError();
      LogManager.info("User clicked restart from RTMS error modal - restarting summarization");
      start({ isRestartAttempt: true });
    },
  };

  return {
    // state read in render / passed to children
    summarizeState,
    isRtmsStartedByOther,
    rtmsRequesterName,
    rtmsStreamId,
    meetingAgendaActive,
    meetingActionItemsActive,
    summarizeClickCount,
    isProductFeedbackModalOpen,
    setIsProductFeedbackModalOpen,
    aimsUuid,
    summaryData,
    // ready-to-spread bundle for <ErrorModals>
    errorModals,
    // refs read during render
    rtmsStartTimestampRef,
    // actions
    start,
    end,
  };
};

/**
 * useStartedByStatus - owns the "started by" status on the Assistant tab: who else started the
 * session and what was turned on. Shows while a session someone else started is active (not for
 * this user's own start) and auto-dismisses after 20s. With no chat messages it sits above the
 * prompt buttons; otherwise it is a chat-log entry at `shownAt`. dismissStartedBy lets the Assistant
 * drop it early (new chat session).
 *
 * @returns {{ startedBy: { name, description, shownAt } | null, dismissStartedBy }}
 */
export const useStartedByStatus = ({ summarizeState, isRtmsStartedByOther, rtmsRequesterName, description }) => {
  const [shownAt, setShownAt] = useState(null);
  const isSummarizing = summarizeState === SUMMARIZE_STATES.SUMMARIZING;

  useEffect(() => {
    setShownAt(isSummarizing && isRtmsStartedByOther ? Date.now() : null);
  }, [isSummarizing, isRtmsStartedByOther, rtmsRequesterName]);

  useEffect(() => {
    if (!shownAt) return;
    const timer = setTimeout(() => setShownAt(null), STARTED_BY_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [shownAt]);

  return {
    startedBy: shownAt ? { name: rtmsRequesterName || RTMS_REQUESTER_DEFAULT_NAME, description, shownAt } : null,
    dismissStartedBy: () => setShownAt(null),
  };
};

/**
 * useHomeNavigation - owns Home's view-navigation state machine: which top-level view is showing
 * (main / summaries / summary-detail / settings), the selected summary, and the active session
 * tab, plus the click handlers that move between them. setView / setSelectedTab are exposed for
 * the few cross-cutting transitions Home still drives (end/session-reset/settings shortcuts).
 */
export const useHomeNavigation = () => {
  const [view, setView] = useState(VIEWS.MAIN);
  const [selectedTab, setSelectedTab] = useState(SESSION_TABS.ASSISTANT);
  const [selectedSummary, setSelectedSummary] = useState(null);

  const handleSummariesClick = () => {
    if (view === VIEWS.SUMMARIES || view === VIEWS.SUMMARY_DETAIL) {
      setView(VIEWS.MAIN);
      setSelectedSummary(null);
    } else {
      setView(VIEWS.SUMMARIES);
    }
  };

  const handleSelectSummary = (summary) => {
    setSelectedSummary(summary);
    setView(VIEWS.SUMMARY_DETAIL);
  };

  const handleBackToSummaries = () => {
    setSelectedSummary(null);
    setView(VIEWS.SUMMARIES);
  };

  const handleSettingsClick = () => {
    setView(view === VIEWS.SETTINGS ? VIEWS.MAIN : VIEWS.SETTINGS);
  };

  const handleTabClick = (tabId) => () => {
    LogManager.metric(`Session_Tab_Clicked_${tabId}`, 1);
    setSelectedTab(tabId);
    setView(VIEWS.MAIN);
  };

  return {
    view,
    setView,
    selectedTab,
    setSelectedTab,
    selectedSummary,
    handleSummariesClick,
    handleSelectSummary,
    handleBackToSummaries,
    handleSettingsClick,
    handleTabClick,
  };
};
